import React, { useState, useEffect, useContext } from "react";
import { API, AuthContext } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  MessageSquare,
  Plus,
  Eye,
  Pencil,
  Share2,
  UserPlus,
  Ban,
  Trash2,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import { ConversationFormDialog } from "./client/ConversationFormDialog";
import { VariationFormDialog } from "./client/VariationFormDialog";
import { SendLinkDialog } from "./client/SendLinkDialog";
import { AddClientDialog } from "./client/AddClientDialog";

const TYPE_LABEL = {
  phone: "Phone",
  on_site: "On Site",
  meeting: "Meeting",
  email: "Email",
  other: "Other",
};
const COST_LABEL = {
  increase: "Increase",
  decrease: "Decrease",
  no_change: "No Change",
  tbc: "TBC",
};

function convStatus(c) {
  if (c.acknowledged) return { label: "Acknowledged", cls: "bg-green-100 text-green-700" };
  if (c.token_active) return { label: "Sent — Pending", cls: "bg-amber-100 text-amber-700" };
  return { label: "Draft", cls: "bg-slate-100 text-slate-600" };
}
function varStatus(v) {
  if (v.client_response === "approved")
    return { label: "Approved", cls: "bg-green-100 text-green-700" };
  if (v.client_response === "rejected")
    return { label: "Rejected", cls: "bg-rose-100 text-rose-700" };
  if (v.status === "superseded")
    return { label: "Superseded", cls: "bg-slate-100 text-slate-600" };
  if (v.token_active) return { label: "Sent — Pending", cls: "bg-amber-100 text-amber-700" };
  return { label: "Draft", cls: "bg-slate-100 text-slate-600" };
}

export default function ClientComms() {
  const { user } = useContext(AuthContext);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [tab, setTab] = useState("conversations");
  const [conversations, setConversations] = useState([]);
  const [variations, setVariations] = useState([]);

  const [convForm, setConvForm] = useState({ open: false, edit: null });
  const [varForm, setVarForm] = useState({ open: false, edit: null });
  const [view, setView] = useState(null); // {kind, record}
  const [sendLink, setSendLink] = useState({ open: false, kind: null, record: null, url: "" });
  const [confirmDelete, setConfirmDelete] = useState(null); // {kind, record}
  const [clientDialog, setClientDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // "conversation" | "variation" | null

  const token = () => localStorage.getItem("token");
  const isAdmin = user?.role === "admin";
  const canAccess = user?.role === "admin" || user?.role === "company_owner" || user?.role === "site_manager";

  const currentSite = sites.find((s) => s.id === selectedSite) || null;
  const hasClient = !!currentSite?.client_name;
  const defaultClient = currentSite
    ? {
        client_name: currentSite.client_name || "",
        client_email: currentSite.client_email || "",
        client_phone: currentSite.client_phone || "",
      }
    : null;

  useEffect(() => {
    if (canAccess) loadSites();
  }, []); // eslint-disable-line

  const loadSites = async () => {
    const res = await fetch(`${API}/sites`, {
      headers: { Authorization: `Bearer ${token()}` },
    });
    if (res.ok) {
      const data = await res.json();
      const sorted = data.sort((a, b) => a.name.localeCompare(b.name));
      setSites(sorted);
      if (sorted.length && !selectedSite) {
        const saved = localStorage.getItem("clientCommsSite");
        const initial = sorted.find((s) => s.id === saved) ? saved : sorted[0].id;
        setSelectedSite(initial);
        loadData(initial);
      }
    }
  };

  const loadData = async (siteId) => {
    if (!siteId) return;
    const [cRes, vRes] = await Promise.all([
      fetch(`${API}/client-comms/${siteId}`, {
        headers: { Authorization: `Bearer ${token()}` },
      }),
      fetch(`${API}/client-variations/${siteId}`, {
        headers: { Authorization: `Bearer ${token()}` },
      }),
    ]);
    setConversations(cRes.ok ? await cRes.json() : []);
    setVariations(vRes.ok ? await vRes.json() : []);
  };

  const onSiteChange = (id) => {
    setSelectedSite(id);
    localStorage.setItem("clientCommsSite", id);
    loadData(id);
  };

  const refresh = () => loadData(selectedSite);

  const downloadVariationPdf = async (variation) => {
    try {
      const res = await fetch(`${API}/client-variations/${variation.id}/report-pdf`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) {
        toast.error("Could not generate PDF");
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Variation_${variation.variation_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate PDF");
    }
  };

  const onClientSaved = (updatedSite) => {
    setSites((prev) => prev.map((s) => (s.id === updatedSite.id ? updatedSite : s)));
    // If we opened the client dialog en route to logging a record, continue to that form now.
    if (pendingAction === "conversation") setConvForm({ open: true, edit: null });
    if (pendingAction === "variation") setVarForm({ open: true, edit: null });
    setPendingAction(null);
  };

  const startLog = (kind) => {
    if (!selectedSite) return;
    if (!hasClient) {
      setPendingAction(kind);
      setClientDialog(true);
      return;
    }
    if (kind === "conversation") setConvForm({ open: true, edit: null });
    else setVarForm({ open: true, edit: null });
  };

  const doSendLink = async (kind, record) => {
    const endpoint =
      kind === "conversation"
        ? `${API}/client-comms/${record.id}/send-link`
        : `${API}/client-variations/${record.id}/send-link`;
    const res = await fetch(endpoint, {
      method: "POST",
      headers: { Authorization: `Bearer ${token()}` },
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      toast.error(e.detail || "Could not generate link");
      return;
    }
    const data = await res.json();
    const url = `${window.location.origin}${data.path}`;
    await refresh();
    setSendLink({ open: true, kind, record, url });
  };

  const doRevoke = async (kind, record) => {
    const endpoint =
      kind === "conversation"
        ? `${API}/client-comms/${record.id}/revoke`
        : `${API}/client-variations/${record.id}/revoke`;
    const res = await fetch(endpoint, {
      method: "POST",
      headers: { Authorization: `Bearer ${token()}` },
    });
    if (res.ok) {
      toast.success("Link revoked");
      refresh();
    } else {
      toast.error("Could not revoke");
    }
  };

  const doDelete = async () => {
    const { kind, record } = confirmDelete;
    const endpoint =
      kind === "conversation"
        ? `${API}/client-comms/${record.id}`
        : `${API}/client-variations/${record.id}`;
    const res = await fetch(endpoint, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token()}` },
    });
    if (res.ok) {
      toast.success("Deleted");
      refresh();
    } else {
      const e = await res.json().catch(() => ({}));
      toast.error(e.detail || "Could not delete");
    }
    setConfirmDelete(null);
  };

  if (!canAccess) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center" data-testid="client-comms-denied">
        <h2 className="text-2xl font-bold text-slate-800">Client Comms</h2>
        <p className="text-slate-500 mt-2">
          This module is available to Admins and Site Managers only.
        </p>
      </div>
    );
  }

  const ackCount = conversations.filter((c) => c.acknowledged).length;
  const pendingConv = conversations.filter((c) => c.token_active && !c.acknowledged).length;
  const pendingVar = variations.filter((v) => v.token_active && !v.client_response).length;
  const approved = variations.filter((v) => v.client_response === "approved").length;
  const rejected = variations.filter((v) => v.client_response === "rejected").length;

  const Stat = ({ label, value, testid }) => (
    <div className="flex-1 min-w-[120px]" data-testid={testid}>
      <p className="text-2xl font-bold text-slate-800">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="client-comms-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-amber-600 flex items-center justify-center">
          <MessageSquare className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Client Comms</h1>
          <p className="text-sm text-slate-500">
            Log conversations & variations — client verifies via a secure link
          </p>
        </div>
      </div>

      <div className="mb-5 max-w-sm">
        <Select value={selectedSite} onValueChange={onSiteChange}>
          <SelectTrigger data-testid="client-comms-site-select">
            <SelectValue placeholder="Select a site" />
          </SelectTrigger>
          <SelectContent>
            {sites.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {currentSite && (
        <Card className="mb-5" data-testid="client-banner">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
            {hasClient ? (
              <div className="text-sm">
                <p className="text-xs uppercase tracking-wide text-slate-400">Client</p>
                <p className="font-semibold text-slate-800" data-testid="client-banner-name">
                  {currentSite.client_name}
                </p>
                <p className="text-slate-500">
                  {[currentSite.client_email, currentSite.client_phone]
                    .filter(Boolean)
                    .join(" · ") || "No contact details"}
                </p>
                {currentSite.client_notes && (
                  <p className="text-slate-400 italic mt-0.5">{currentSite.client_notes}</p>
                )}
              </div>
            ) : (
              <div className="text-sm text-slate-500">
                No client set for this site yet — add one to log conversations or variations.
              </div>
            )}
            <Button
              variant="outline"
              onClick={() => {
                setPendingAction(null);
                setClientDialog(true);
              }}
              data-testid="manage-client-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {hasClient ? "Edit Client" : "Add Client"}
            </Button>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="conversations" data-testid="tab-conversations">
            Conversations
          </TabsTrigger>
          <TabsTrigger value="variations" data-testid="tab-variations">
            Variations
          </TabsTrigger>
        </TabsList>

        {/* CONVERSATIONS */}
        <TabsContent value="conversations" className="mt-4">
          <Card className="mb-4">
            <CardContent className="flex flex-wrap gap-4 pt-6">
              <Stat label="Total Conversations" value={conversations.length} testid="stat-conv-total" />
              <Stat label="Pending Acknowledgement" value={pendingConv} testid="stat-conv-pending" />
              <Stat label="Acknowledged" value={ackCount} testid="stat-conv-ack" />
            </CardContent>
          </Card>

          <div className="flex justify-end mb-3">
            <Button
              onClick={() => startLog("conversation")}
              disabled={!selectedSite}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="log-conversation-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Log Conversation
            </Button>
          </div>

          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="p-3">Date</th>
                    <th className="p-3">Client</th>
                    <th className="p-3">Type</th>
                    <th className="p-3">Subject</th>
                    <th className="p-3">Status</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {conversations.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-6 text-center text-slate-400">
                        No conversations logged for this site yet.
                      </td>
                    </tr>
                  )}
                  {conversations.map((c) => {
                    const st = convStatus(c);
                    const locked = c.acknowledged || c.token_active;
                    return (
                      <tr key={c.id} className="border-t" data-testid={`conv-row-${c.id}`}>
                        <td className="p-3 whitespace-nowrap">{c.date}</td>
                        <td className="p-3">{c.client_name}</td>
                        <td className="p-3">{TYPE_LABEL[c.type]}</td>
                        <td className="p-3 max-w-[220px] truncate">{c.subject}</td>
                        <td className="p-3">
                          <Badge className={`${st.cls} border-0`}>{st.label}</Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" title="View"
                              onClick={() => setView({ kind: "conversation", record: c })}
                              data-testid={`conv-view-${c.id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            {!locked && (
                              <Button variant="ghost" size="icon" title="Edit"
                                onClick={() => setConvForm({ open: true, edit: c })}
                                data-testid={`conv-edit-${c.id}`}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                            )}
                            {!c.acknowledged && (
                              <Button variant="ghost" size="icon" title="Send Link"
                                onClick={() => doSendLink("conversation", c)}
                                data-testid={`conv-send-${c.id}`}>
                                <Share2 className="w-4 h-4" />
                              </Button>
                            )}
                            {c.token_active && !c.acknowledged && (
                              <Button variant="ghost" size="icon" title="Revoke"
                                onClick={() => doRevoke("conversation", c)}
                                data-testid={`conv-revoke-${c.id}`}>
                                <Ban className="w-4 h-4 text-rose-500" />
                              </Button>
                            )}
                            {isAdmin && !c.token_active && (
                              <Button variant="ghost" size="icon" title="Delete"
                                onClick={() => setConfirmDelete({ kind: "conversation", record: c })}
                                data-testid={`conv-delete-${c.id}`}>
                                <Trash2 className="w-4 h-4 text-rose-500" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* VARIATIONS */}
        <TabsContent value="variations" className="mt-4">
          <Card className="mb-4">
            <CardContent className="flex flex-wrap gap-4 pt-6">
              <Stat label="Total Variations" value={variations.length} testid="stat-var-total" />
              <Stat label="Pending" value={pendingVar} testid="stat-var-pending" />
              <Stat label="Approved" value={approved} testid="stat-var-approved" />
              <Stat label="Rejected" value={rejected} testid="stat-var-rejected" />
            </CardContent>
          </Card>

          <div className="flex justify-end mb-3">
            <Button
              onClick={() => startLog("variation")}
              disabled={!selectedSite}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="log-variation-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Log Variation
            </Button>
          </div>

          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="p-3">Variation #</th>
                    <th className="p-3">Date</th>
                    <th className="p-3">Client</th>
                    <th className="p-3">Description</th>
                    <th className="p-3">Cost</th>
                    <th className="p-3">Status</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {variations.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-6 text-center text-slate-400">
                        No variations logged for this site yet.
                      </td>
                    </tr>
                  )}
                  {variations.map((v) => {
                    const st = varStatus(v);
                    const locked = !!v.client_response || v.token_active;
                    return (
                      <tr key={v.id} className="border-t" data-testid={`var-row-${v.id}`}>
                        <td className="p-3 font-semibold whitespace-nowrap">{v.variation_number}</td>
                        <td className="p-3 whitespace-nowrap">{v.date_raised}</td>
                        <td className="p-3">{v.client_name}</td>
                        <td className="p-3 max-w-[220px] truncate">{v.description}</td>
                        <td className="p-3">{COST_LABEL[v.cost_impact]}</td>
                        <td className="p-3">
                          <Badge className={`${st.cls} border-0`}>{st.label}</Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" title="View"
                              onClick={() => setView({ kind: "variation", record: v })}
                              data-testid={`var-view-${v.id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            {!locked && (
                              <Button variant="ghost" size="icon" title="Edit"
                                onClick={() => setVarForm({ open: true, edit: v })}
                                data-testid={`var-edit-${v.id}`}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                            )}
                            {!v.client_response && (
                              <Button variant="ghost" size="icon" title="Send Link"
                                onClick={() => doSendLink("variation", v)}
                                data-testid={`var-send-${v.id}`}>
                                <Share2 className="w-4 h-4" />
                              </Button>
                            )}
                            {v.token_active && !v.client_response && (
                              <Button variant="ghost" size="icon" title="Revoke"
                                onClick={() => doRevoke("variation", v)}
                                data-testid={`var-revoke-${v.id}`}>
                                <Ban className="w-4 h-4 text-rose-500" />
                              </Button>
                            )}
                            {isAdmin && !v.token_active && (
                              <Button variant="ghost" size="icon" title="Delete"
                                onClick={() => setConfirmDelete({ kind: "variation", record: v })}
                                data-testid={`var-delete-${v.id}`}>
                                <Trash2 className="w-4 h-4 text-rose-500" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Form dialogs */}
      <ConversationFormDialog
        open={convForm.open}
        editRecord={convForm.edit}
        siteId={selectedSite}
        defaultClient={defaultClient}
        onClose={() => setConvForm({ open: false, edit: null })}
        onSaved={refresh}
      />
      <VariationFormDialog
        open={varForm.open}
        editRecord={varForm.edit}
        siteId={selectedSite}
        conversations={conversations}
        defaultClient={defaultClient}
        onClose={() => setVarForm({ open: false, edit: null })}
        onSaved={refresh}
      />
      <AddClientDialog
        open={clientDialog}
        site={currentSite}
        onClose={() => {
          setClientDialog(false);
          setPendingAction(null);
        }}
        onSaved={onClientSaved}
      />
      <SendLinkDialog
        open={sendLink.open}
        kind={sendLink.kind}
        record={sendLink.record}
        url={sendLink.url}
        onClose={() => setSendLink({ open: false, kind: null, record: null, url: "" })}
      />

      {/* View dialog */}
      <Dialog open={!!view} onOpenChange={() => setView(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="record-view-dialog">
          {view?.kind === "conversation" && (
            <>
              <DialogHeader>
                <DialogTitle>{view.record.subject || "Conversation"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                <p><b>Client:</b> {view.record.client_name} · {view.record.client_email}</p>
                <p><b>Date:</b> {view.record.date} · <b>Type:</b> {TYPE_LABEL[view.record.type]}</p>
                <p className="whitespace-pre-wrap"><b>Summary:</b> {view.record.summary}</p>
                <p className="text-slate-500">Logged by {view.record.logged_by_name}</p>
                {view.record.acknowledged && (
                  <p className="text-green-700">Acknowledged on {new Date(view.record.acknowledged_at).toLocaleString("en-AU")}</p>
                )}
              </div>
            </>
          )}
          {view?.kind === "variation" && (
            <>
              <DialogHeader>
                <DialogTitle>Variation {view.record.variation_number}</DialogTitle>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                <p><b>Client:</b> {view.record.client_name} · {view.record.client_email}</p>
                <p><b>Date raised:</b> {view.record.date_raised} · <b>Raised by:</b> {view.record.raised_by}</p>
                <p className="whitespace-pre-wrap"><b>Description:</b> {view.record.description}</p>
                <p><b>Cost impact:</b> {COST_LABEL[view.record.cost_impact]}{view.record.cost_amount != null ? ` ($${view.record.cost_amount})` : ""}</p>
                <p><b>Timeline impact:</b> {view.record.timeline_impact}{view.record.timeline_days != null ? ` (${view.record.timeline_days} days)` : ""}</p>
                {view.record.builder_notes && (
                  <p className="text-rose-600 whitespace-pre-wrap"><b>Builder notes (internal):</b> {view.record.builder_notes}</p>
                )}
                {view.record.client_response && (
                  <p className={view.record.client_response === "approved" ? "text-green-700" : "text-rose-700"}>
                    Client {view.record.client_response} on {new Date(view.record.responded_at).toLocaleString("en-AU")}
                    {view.record.client_note ? ` — "${view.record.client_note}"` : ""}
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => downloadVariationPdf(view.record)}
                  data-testid="download-variation-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" /> Download Variation PDF
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the draft. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
